import { Box, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { AuthoringBoundEditableRichText } from "authoring/components/authoring-editable-rich-text"
import { createSlot } from "lib/@components/slot/create-slot"
import { If } from "lib/@components/switch"
import { useMeasurement } from "lib/@hooks/useMeasurement"
import { htmlToText } from "lib/html-to-text"
import { useDarkMode } from "lib/use-dark-mode"
import { normalizeActions } from "library/normalizeActions"
import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { ScheduleIntroductionContentAfter } from "slot-definitions"

export const ScheduleInfoNotes = createSlot("ScheduleInfoNotes")

export function ScheduleIntroNotes() {
    const isDark = useDarkMode()
    const { projectId, groupId } = useParams()
    const textColor = isDark ? "info.lighter" : "info.darker"
    const { schedule } = useBoundContext()
    const [showMore, setShowMore] = useState(false)
    const [overflowing, setOverflowing] = useState(false)
    const hasContent = !!htmlToText(schedule?.intro?.content).trim().length
    const hasNotes = !!htmlToText(schedule?.intro?.note).trim().length
    const authoringLink = `/authoring/projects/${projectId}/${groupId}/correlator/34164`

    const maxHeight = 50
    // eslint-disable-next-line no-unused-vars
    const [{ width }, attach, _, element] = useMeasurement()

    useEffect(() => {
        if (!element.current) return
        if (element.current.scrollHeight > maxHeight) {
            setOverflowing(true)
        } else {
            setOverflowing(false)
        }
    }, [width, element])

    return (
        hasContent && (
            <Bound
                target={schedule}
                showMore={showMore}
                setShowMore={setShowMore}
                overflowing={overflowing}
                overrideLink={authoringLink}
                code={schedule?.id}
            >
                <Stack my={3} pl={1} pb={1}>
                    <Stack spacing={1} data-cy="schedule-introduction-notes">
                        <Typography variant="subtitle1" sx={{ textTransform: "uppercase" }}>
                            Introduction
                        </Typography>
                        <Stack
                            ref={attach}
                            sx={{ maxHeight: showMore ? "" : maxHeight, overflow: "hidden", color: textColor }}
                        >
                            <AuthoringBoundEditableRichText
                                label="Introduction"
                                field="intro.content"
                                transformIn={normalizeActions}
                            />
                        </Stack>
                        <ScheduleIntroductionContentAfter.Slot predicate={() => !!showMore || !overflowing} />
                        <If truthy={showMore && hasNotes}>
                            <Box sx={{ mt: 2, pl: 2, pr: { lg: 16, md: 4, xs: 0, fontSize: 14 } }}>
                                <Typography variant="overline">Notes</Typography>
                                <Box sx={{ color: textColor }}>
                                    <AuthoringBoundEditableRichText label="Notes" field="intro.note" />
                                </Box>
                            </Box>
                        </If>
                        <ScheduleInfoNotes.Slot />
                    </Stack>
                </Stack>
            </Bound>
        )
    )
}
